import { useAddSpaceModal } from "@circle-react/components/Modals/AddSpaceModal";
import { useEditSpaceGroupModal } from "@circle-react/components/Modals/EditSpaceGroupModal";
import type { SpaceGroupWithSpaces } from "@circle-react/components/Spaces/Sidebar";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { canManageSpaceGroups } from "@circle-react/helpers/spaceGroupHelpers";
import { spaceGroupPaths } from "@circle-react/helpers/urlHelpers";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation } from "react-query";

import { classNames } from "@/react/helpers/twMergeWithCN";
import { useLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";

import { usePlatformMenu } from "../../PlatformMenu/usePlatformMenu";
import { SpaceList } from "../SpaceList";
import { SpaceGroupView } from "./SpaceGroupView";

export interface SpaceGroupProps {
  position: number;
  spaceGroup: SpaceGroupWithSpaces;
}

export const SpaceGroup = ({ spaceGroup, position }: SpaceGroupProps) => {
  const { currentCommunityMember } = usePunditUserContext();
  const [isCollapsed, toggleIsCollapsed] = useBoolean(spaceGroup.collapsed);
  const editSpaceGroupModal = useEditSpaceGroupModal();
  const spaceModal = useAddSpaceModal();
  const { toggleSpaceGroupDrawer } = usePlatformMenu();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const isLargeScreen = useLgScreenMediaQuery();
  const shouldDisableDragAndDropForSpaceGroups = !isAdmin || !isLargeScreen;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
    isDragging,
  } = useSortable({
    id: `space-group-${spaceGroup.id}`,
    disabled: shouldDisableDragAndDropForSpaceGroups,
    data: {
      type: "spaceGroup",
      id: spaceGroup.id,
    },
  });

  const { mutate: toggleVisibility } = useMutation({
    mutationFn: () =>
      reactQueryPost(
        spaceGroupPaths.toggleVisibility({
          spaceGroupId: String(spaceGroup.id),
        }),
      ),
  });

  const canManage = canManageSpaceGroups({ spaceGroup });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const handleCollapseToggle = () => {
    toggleIsCollapsed();
    toggleVisibility();
  };

  const showMembersModal = () =>
    editSpaceGroupModal.show({
      spaceGroup,
      tab: "Members",
    });

  const showCreateSpaceModal = () =>
    spaceModal.show({ spaceGroupId: spaceGroup.id });

  const showEditSpaceGroupModal = () =>
    editSpaceGroupModal.show({
      spaceGroup,
      tab: "Details",
    });

  return (
    <SpaceGroupView
      id={spaceGroup.slug}
      position={position}
      isCollapsed={isCollapsed}
      isDragging={isDragging}
      canManage={canManage}
      onCollapseToggle={handleCollapseToggle}
      spaceGroup={spaceGroup}
      onEditSpaceGroupClick={showEditSpaceGroupModal}
      onCreateSpaceClick={showCreateSpaceModal}
      onCreateSpaceGroupClick={toggleSpaceGroupDrawer}
      isAdmin={isAdmin}
      onAddMembersClick={showMembersModal}
      ref={setNodeRef}
      viewWrapperRef={setActivatorNodeRef}
      style={style}
      className={classNames({
        "pointer-events-none opacity-50": isDragging,
      })}
      {...attributes}
      {...listeners}
    >
      <SpaceList spaceGroup={spaceGroup} />
    </SpaceGroupView>
  );
};
